/* «Партнёрство» в серверном режиме: снимок программы, друзья, каталог материалов и выводы с сервера. Разметку и движение рисует шаблон (engine.ts),
   он читает данные отсюда и перерисовывается по changed(). Финансовые и отправляющие запросы идут через намерения в sessionStorage:
   пока исход не подтверждён, новый запрос того же вида заблокирован до проверки прежнего с тем же ключом. */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type {
  PartnerFilter,
  PartnerFriend,
  PartnerMaterialCatalog,
  PartnerMaterialInput,
  PartnerMaterialKind,
  PartnerMaterialResult,
  PartnerOverview,
  PartnerPeriod,
  PartnerWithdrawal,
} from '../../api/partner';
import { storyBackgrounds } from '../../api/partner';
import type { Me } from '../../api/types';
import { UploadTransportError } from '../../api/uploads';
import { applyPartnerRef, partnerLevels, serveStories } from '../../bridge/partner-ref';
import { uiFormat, uiPlural, uiText } from '../../i18n';
import {
  clearMaterialIntent,
  definitiveMaterialError,
  materialPhase,
  readMaterialIntent,
  saveMaterialIntent,
  type MaterialIntent,
} from './material-intent';
import {
  clearWithdrawalIntent,
  definitiveWithdrawalError,
  readWithdrawalIntent,
  saveWithdrawalIntent,
  validateInvoiceFile,
  validateWithdrawal,
  type WithdrawalBody,
  type WithdrawalIntent,
} from './withdrawal-intent';

type Tip = (el: Element | null | undefined, text: string) => void;
/** Сколько раз и как часто спрашивать готовность баннера (около 12 с), дальше — «повторите» с тем же ключом */
const BANNER_POLLS = 8;
const BANNER_POLL_MS = 1500;
/** Окончательный ответ по материалу: намерение уже снято, текст — для пользователя */
class MaterialFinal extends Error {}
type FriendsPage = { items: PartnerFriend[]; cursor: string | null; busy: boolean; err: string };

/** Уровень в виде шаблона: процент, с какого числа покупателей, можно ли выводить */
export interface ServerTier {
  id: string;
  name: string;
  pct: number;
  from: number;
  out: boolean;
}

/** Срок участия по графику «Всего времени» (столбики по месяцам): от первого месяца с начислениями до текущего — как monthsIn шаблона;
    0 — график не по месяцам или начислений нет */
export function participationMonths(o: Pick<PartnerOverview, 'chart' | 'chart_unit'> | null | undefined): number {
  if (!o || o.chart_unit !== 'month') return 0;
  const i = o.chart.findIndex(Boolean);
  return i < 0 ? 0 : o.chart.length - i;
}
/** Подпись под числами «Обзора»: новый партнёр — «Пока нет данных»; «Всё время» — срок участия («за 9 месяцев»), если он известен; иначе период.
    На языке профиля (src/i18n), шаблон тот же, что у шаблона раздела */
export function kpiNote(period: PartnerPeriod, fresh: boolean, months: number): string {
  if (fresh) return uiText('Пока нет данных');
  if (period === 'all')
    return months ? uiFormat('за {0} {1}', months, uiPlural(months, 'месяц', 'месяца', 'месяцев')) : uiText('за всё время');
  return uiText(period === 'week' ? 'за неделю' : 'за месяц');
}

const withdrawText = (error: unknown) => {
  if (error instanceof ApiError) {
    if (error.code === 'steos_id_required') return 'Для вывода войдите через STEOS ID';
    if (error.code === 'withdraw_level_required') return 'Вывод недоступен на текущем уровне';
    if (error.code === 'withdraw_exceeds') return 'Сумма больше доступной';
    if (error.code === 'withdraw_below_min') return 'Сумма меньше минимальной';
    if (error.code === 'requisites_invalid') return 'Проверьте реквизиты и счёт';
    if (error.code === 'withdrawal_invoice_not_ready') return 'Счёт ещё не проверен. Загрузите его заново';
    if (error.code === 'partner_production_payout_disabled' || error.code === 'partner_payout_policy_missing')
      return 'Вывод сейчас недоступен';
  }
  return 'Ответ не получен. Проверьте ту же заявку: её данные и ключ сохранены';
};

export function createPartnerServer(changed: () => void, tip: Tip) {
  const st = {
    owner: null as number | null,
    ready: false,
    err: '',
    blocked: false,
    ov: {} as Partial<Record<PartnerPeriod, PartnerOverview>>,
    fr: {} as Partial<Record<PartnerFilter, FriendsPage>>,
    counts: null as Record<PartnerFilter, number> | null,
    friendErrors: {} as Partial<Record<PartnerFilter, string>>,
    friendLoads: {} as Partial<Record<PartnerFilter, Promise<boolean>>>,
    hist: null as PartnerWithdrawal[] | null,
    cat: null as PartnerMaterialCatalog | null,
    wd: null as WithdrawalIntent | null,
    mat: null as MaterialIntent | null,
    running: false,
    ctl: null as AbortController | null,
  };
  const week = () => st.ov.week ?? null;

  /** Владелец из проверенной сессии; другой аккаунт — кэш прошлого не показывается */
  function own(me: Me) {
    if (st.owner !== null && st.owner !== me.user.id) {
      st.ov = {};
      st.fr = {};
      st.friendLoads = {};
      st.counts = null;
      st.friendErrors = {};
      st.friendLoads = {};
      st.hist = null;
      st.cat = null;
    }
    st.owner = me.user.id;
  }

  function readIntents(owner: number) {
    try {
      st.wd = readWithdrawalIntent(owner);
      st.mat = readMaterialIntent(owner);
      st.blocked = false;
    } catch {
      st.wd = null;
      st.mat = null;
      st.blocked = true;
    }
  }

  function putFriends(
    f: PartnerFilter,
    page: { items: PartnerFriend[]; counts: Record<PartnerFilter, number>; next_cursor: string | null },
  ) {
    st.fr[f] = { items: page.items, cursor: page.next_cursor, busy: false, err: '' };
    st.counts = page.counts;
  }

  /** Снимок программы: обзор недели, профиль, первая страница друзей, выводы и каталог одним заходом */
  async function load() {
    st.ctl?.abort();
    const ctl = new AbortController();
    st.ctl = ctl;
    /* Повторная загрузка (вход в раздел, возврат в приложение) обновляет показанное на месте: заготовки и новое появление колонки —
       только у первой. Смена владельца по-прежнему сбрасывает всё (own) */
    const sessionOwner = (window as unknown as { BotServerMe?: { user?: { id?: number } } }).BotServerMe?.user?.id;
    if (!st.ov.week || st.owner === null || st.owner !== sessionOwner) st.ready = false;
    st.err = '';
    try {
      const [ov, me, fr, hist, cat] = await Promise.all([
        endpoints.partner.overview('week', ctl.signal),
        endpoints.me(),
        endpoints.partner.friends('all', undefined, ctl.signal).catch(() => null),
        endpoints.partner.withdrawals(ctl.signal).catch(() => null),
        endpoints.partner.materials(ctl.signal).catch(() => null),
      ]);
      if (ctl.signal.aborted) return;
      own(me);
      st.ov = { week: ov };
      st.fr = {};
      st.friendLoads = {};
      st.counts = null;
      st.friendErrors = fr ? {} : { all: 'Не удалось загрузить список друзей' };
      if (fr) putFriends('all', fr);
      st.hist = hist?.items ?? null;
      st.cat = cat;
      window.BotCore?.story?.only(cat?.items.filter(x => x.kind === 'story' && x.available).map(x => x.id) ?? []);
      window.BotCore?.story?.bg(storyBackgrounds(cat));
      applyPartnerRef(ov);
      readIntents(me.user.id);
      st.ready = true;
      st.err = st.blocked ? 'Не удалось восстановить прежнюю заявку. Напишите в поддержку перед новой' : '';
    } catch {
      if (ctl.signal.aborted) return;
      st.err = 'Партнёрская программа временно недоступна';
    }
    changed();
  }

  /** Обзор другого периода; уже загруженный не запрашивается */
  async function period(p: PartnerPeriod) {
    if (st.ov[p]) return true;
    const owner = st.owner,
      ctl = st.ctl;
    try {
      const value = await endpoints.partner.overview(p, ctl?.signal);
      if (owner !== st.owner || ctl !== st.ctl || ctl?.signal.aborted) return false;
      st.ov[p] = value;
      return true;
    } catch {
      return false;
    }
  }

  /** Первая страница друзей под фильтром */
  function friends(f: PartnerFilter): Promise<boolean> {
    if (st.fr[f]) return Promise.resolve(true);
    if (st.friendLoads[f]) return st.friendLoads[f];
    const owner = st.owner,
      ctl = st.ctl;
    delete st.friendErrors[f];
    const request = endpoints.partner
      .friends(f, undefined, ctl?.signal)
      .then(page => {
        if (owner !== st.owner || ctl !== st.ctl || ctl?.signal.aborted) return false;
        putFriends(f, page);
        return true;
      })
      .catch(() => {
        if (owner === st.owner && ctl === st.ctl && !ctl?.signal.aborted) st.friendErrors[f] = 'Не удалось загрузить список друзей';
        return false;
      })
      .finally(() => {
        if (st.friendLoads[f] === request) delete st.friendLoads[f];
      });
    st.friendLoads[f] = request;
    return request;
  }

  async function history() {
    const owner = st.owner,
      ctl = st.ctl;
    try {
      const page = await endpoints.partner.withdrawals(ctl?.signal);
      if (owner !== st.owner || ctl !== st.ctl || ctl?.signal.aborted) return false;
      st.hist = page.items;
      return true;
    } catch {
      return false;
    } finally {
      changed();
    }
  }

  /** Следующая страница: строки добавляются к уже показанным */
  async function more(f: PartnerFilter) {
    const page = st.fr[f];
    if (!page?.cursor || page.busy) return false;
    page.busy = true;
    page.err = '';
    const owner = st.owner,
      ctl = st.ctl;
    try {
      const next = await endpoints.partner.friends(f, page.cursor, ctl?.signal);
      if (owner !== st.owner || ctl !== st.ctl || ctl?.signal.aborted || st.fr[f] !== page) return false;
      page.items = page.items.concat(next.items);
      page.cursor = next.next_cursor;
      st.counts = next.counts;
      return true;
    } catch {
      page.err = 'Не удалось загрузить следующую страницу';
      return false;
    } finally {
      page.busy = false;
    }
  }

  function has(kind: PartnerMaterialKind, id: string) {
    return !!st.cat?.available && st.cat.items.some(x => x.kind === kind && x.id === id && x.available);
  }

  /** Баннер бот готовит в очереди: тот же ключ возвращает ту же операцию, пока она pending — спрашиваем ещё несколько раз */
  async function receiptOf(intent: MaterialIntent) {
    for (let i = 0; ; i++) {
      const receipt = await endpoints.partner.material(intent.kind, intent.input, intent.key);
      if (intent.kind !== 'banner' || receipt.state !== 'pending' || i >= BANNER_POLLS) return receipt;
      await new Promise(done => setTimeout(done, BANNER_POLL_MS));
    }
  }

  const drop = (intent: MaterialIntent, text: string) => {
    clearMaterialIntent(intent.owner);
    st.mat = null;
    return new MaterialFinal(text);
  };

  async function sendMaterial(intent: MaterialIntent, first: boolean) {
    let persisted = false;
    try {
      saveMaterialIntent(intent);
      persisted = true;
      st.mat = intent;
      const receipt = await receiptOf(intent);
      /* очередь бота ответила окончательно: подготовка не удалась или сообщение устарело — новый запрос безопасен */
      if (receipt.state === 'failed' || receipt.state === 'expired')
        throw drop(intent, 'Не удалось подготовить баннер. Попробуйте ещё раз');
      if (materialPhase(intent.kind, receipt) === 'unknown') throw new Error('material_receipt_unknown');
      clearMaterialIntent(intent.owner);
      st.mat = null;
      return receipt;
    } catch (cause) {
      if (cause instanceof MaterialFinal) throw cause;
      /* бот отказал до постановки в очередь (нет подтверждённого чата с пользователем) — прежней операции нет */
      if (cause instanceof ApiError && cause.code === 'material_bot_unavailable')
        throw drop(intent, 'Бот не может подготовить материал: откройте чат с ботом и попробуйте ещё раз');
      if (first && definitiveMaterialError(cause)) throw drop(intent, 'Материал сейчас недоступен');
      if (!persisted) st.blocked = true;
      throw new Error('Отправка не подтверждена. Нажмите ещё раз: повтор проверит прежний запрос', { cause });
    }
  }

  const same = (a: MaterialIntent, kind: PartnerMaterialKind, input: PartnerMaterialInput) =>
    a.kind === kind && a.input.template_id === input.template_id && a.input.theme === input.theme;

  /** Материал с вашей ссылкой: баннер (id подготовленного сообщения), постер и рилс (бот пришлёт в чат). Прежняя неподтверждённая отправка проверяется первой */
  async function material(
    kind: PartnerMaterialKind,
    input: PartnerMaterialInput,
    el?: Element | null,
  ): Promise<PartnerMaterialResult | null> {
    if (!st.owner || st.running) return null;
    if (st.blocked) {
      tip(el, 'Прежняя отправка не восстановлена. Напишите в поддержку');
      return null;
    }
    const prev = st.mat;
    if (!prev && (!has(kind, input.template_id ?? '') || !st.cat?.themes.some(t => t.id === input.theme))) {
      tip(el, 'Материал сейчас недоступен');
      return null;
    }
    st.running = true;
    try {
      if (prev && !same(prev, kind, input)) {
        await sendMaterial(prev, false);
        tip(el, 'Прежняя отправка подтверждена. Нажмите ещё раз');
        return null;
      }
      return await sendMaterial(prev ?? { owner: st.owner, key: uuid(), kind, input: { ...input }, phase: 'unknown' }, !prev);
    } catch (cause) {
      tip(el, cause instanceof Error ? cause.message : 'Отправка не подтверждена');
      return null;
    } finally {
      st.running = false;
    }
  }

  /** Счёт на оплату: загрузка с проверкой сервера; обрыв связи — ждём ту же загрузку, файл не отправляется второй раз */
  async function upload(file: File): Promise<{ id: string } | { err: string }> {
    /* перевод — по полной фразе словаря (с точкой), точку в подписи зоны убираем после */
    const invalid = validateInvoiceFile(file);
    if (invalid) return { err: uiText(invalid).replace(/\.$/, '') };
    const intent = { purpose: 'withdrawal_invoice', kind: file.type === 'application/pdf' ? 'document' : 'photo', origin: 'file' } as const;
    try {
      let result;
      try {
        result = await endpoints.uploadFile(file, intent, uuid());
      } catch (cause) {
        if (!(cause instanceof UploadTransportError)) throw cause;
        result = await endpoints.waitForUpload(cause.uploadId);
      }
      if (result.status !== 'ready' || result.purpose !== 'withdrawal_invoice') throw new Error('invoice_not_ready');
      return { id: result.id };
    } catch {
      return { err: 'Счёт не прошёл проверку. Выберите PDF, JPG или PNG до 10 МБ' };
    }
  }

  async function sendWithdrawal(intent: WithdrawalIntent, first: boolean, el?: Element | null) {
    st.running = true;
    try {
      /* сессию могли сменить: прежнее намерение не уходит от имени другого владельца */
      const me = await endpoints.me();
      if (me.user.id !== intent.owner) throw new Error('withdrawal_owner_changed');
      saveWithdrawalIntent(intent);
      st.wd = intent;
    } catch {
      st.running = false;
      st.blocked = true;
      tip(el, 'Не удалось безопасно сохранить заявку. Отправка не выполнена');
      changed();
      return null;
    }
    try {
      const receipt = await endpoints.partner.withdraw(intent.body, intent.key);
      clearWithdrawalIntent(intent.owner);
      st.wd = null;
      st.hist = [receipt, ...(st.hist ?? []).filter(x => x.id !== receipt.id)];
      return receipt;
    } catch (cause) {
      if (first && definitiveWithdrawalError(cause)) {
        clearWithdrawalIntent(intent.owner);
        st.wd = null;
      }
      tip(el, withdrawText(cause));
      return null;
    } finally {
      st.running = false;
      changed();
    }
  }

  /** Заявка на вывод: проверка суммы и реквизитов, намерение сохраняется до POST; после ответа снимок обновляется отдельным чтением */
  async function withdraw(body: WithdrawalBody, el?: Element | null) {
    const ov = week();
    const payee = ov?.withdraw.payees.find(p => p.id === body.payee);
    if (!ov || !payee || !ov.withdraw.terms || !st.owner || st.running || st.wd || st.blocked) return null;
    const invalid = validateWithdrawal(body, ov.withdraw.terms.min, ov.withdraw.withdrawable, payee.currency);
    if (invalid) {
      tip(el, uiText(invalid).replace(/\.$/, ''));
      return null;
    }
    const receipt = await sendWithdrawal({ owner: st.owner, key: uuid(), body }, true, el);
    if (receipt) void load();
    return receipt;
  }

  /** Проверить прежнюю заявку: тот же ключ идемпотентности */
  async function wdRetry(el?: Element | null) {
    if (!st.wd || st.running) return null;
    const receipt = await sendWithdrawal(st.wd, false, el);
    if (receipt) void load();
    return receipt;
  }

  /** Пока раздел на экране: снимок при возвращении в приложение (не во время запроса) */
  const onVisible = () => {
    if (document.visibilityState === 'visible' && !st.running) void load();
  };
  function watch(on: boolean) {
    document.removeEventListener('visibilitychange', onVisible);
    if (on) document.addEventListener('visibilitychange', onVisible);
    else st.ctl?.abort();
  }

  serveStories();

  return {
    load,
    period,
    friends,
    more,
    history,
    friendError: (f: PartnerFilter) => st.friendErrors[f] ?? '',
    material,
    upload,
    withdraw,
    wdRetry,
    watch,
    has,
    ready: () => st.ready,
    err: () => st.err,
    blocked: () => st.blocked,
    ov: (p: PartnerPeriod) => st.ov[p] ?? null,
    week,
    fresh: () => {
      const o = week();
      return !o || (o.friends_all === 0 && o.earned === 0);
    },
    kpiNote: (p: PartnerPeriod) => {
      const o = week();
      return kpiNote(p, !!o && o.friends_all === 0 && o.earned === 0, participationMonths(st.ov.all));
    },
    tiers: (): ServerTier[] => {
      const o = week();
      return o ? partnerLevels(o).map(l => ({ id: l.id, name: l.name, pct: l.pct, from: l.buyers, out: l.withdrawable })) : [];
    },
    payees: () => week()?.withdraw.payees ?? [],
    page: (f: PartnerFilter) => st.fr[f] ?? null,
    counts: () => st.counts,
    hist: () => st.hist ?? [],
    histKnown: () => st.hist !== null,
    wdPending: () => st.wd,
    busy: () => st.running,
  };
}

export type PartnerServer = ReturnType<typeof createPartnerServer>;
